/**
 * Member profile navigation rules the client applies to the /profile-layout
 * tree (TRUS-762).
 *
 * The server (app/services/member_profile/tab_registry.py) owns these: each tab
 * carries `placement` (rail | setup), and retired or folded tabs carry
 * `alias_of` / `segment_of`. The defaults are mirrored here only so that a
 * server that predates TRUS-762, or a failed layout request, still produces the
 * new navigation instead of the old one. A value the payload does carry always
 * wins over these.
 *
 * Nothing here decides VISIBILITY. Every show_* setting, capability and data
 * gate still decides whether a tab shows; placement only says where it is listed.
 */
import type { ProfileLayoutSection, ProfileLayoutTab, ProfilePlacement } from '@/services/api/members';

/** Tabs that live behind the Member setup gear unless the org moved them. */
export const SETUP_TAB_KEYS: readonly string[] = [
  'history',
  'ai-enrichment',
  'consent',
  'notifications',
  'work-compliance',
  'workforce',
  'family',
  'caregivers',
  'households',
  'data-validation',
  'system-information',
];

/** Old keys and where they open now. Mirrors tab_registry.resolve_tab_alias. */
export const FALLBACK_ALIASES: Record<string, { tab_key: string; segment: string | null }> = {
  enrollments: { tab_key: 'programs', segment: null },
  'doula-journey': { tab_key: 'programs', segment: 'doula' },
  'engagement-intel': { tab_key: 'engagement', segment: 'overview' },
  engagements: { tab_key: 'engagement', segment: 'campaigns' },
  achievements: { tab_key: 'engagement', segment: 'incentives' },
  giving: { tab_key: 'engagement', segment: 'giving' },
};

/** The Engagement hub's segments, in their default order. */
export const ENGAGEMENT_SEGMENT_KEYS: readonly string[] = ['engagement-intel', 'engagements', 'achievements', 'giving'];

export interface TabMeta {
  placement: ProfilePlacement;
  aliasOf: string | null;
  segmentOf: string | null;
}

/** The payload's own placement / alias / segment, else the TRUS-762 default. */
export function tabMeta(tab: ProfileLayoutTab): TabMeta {
  const fallbackAlias = FALLBACK_ALIASES[tab.tab_key];
  const isSegment = ENGAGEMENT_SEGMENT_KEYS.includes(tab.tab_key);
  return {
    placement: tab.placement ?? (SETUP_TAB_KEYS.includes(tab.tab_key) ? 'setup' : 'rail'),
    aliasOf: tab.alias_of !== undefined ? tab.alias_of ?? null : !isSegment && fallbackAlias ? fallbackAlias.tab_key : null,
    segmentOf: tab.segment_of !== undefined ? tab.segment_of ?? null : isSegment ? 'engagement' : null,
  };
}

/** Where a requested tab key opens: an alias, an Engagement segment, or itself. */
export function resolveProfileTabRequest(
  key: string,
  aliases?: Record<string, { tab_key: string; segment: string | null }> | null,
): { tab: string; segment: string | null } {
  const hit = aliases?.[key] ?? FALLBACK_ALIASES[key];
  return hit ? { tab: hit.tab_key, segment: hit.segment ?? null } : { tab: key, segment: null };
}

/** Segment id (overview | campaigns | incentives | giving) → the tab key that carries it. */
export function segmentTabKey(segment: string | null | undefined): string | null {
  if (!segment) return null;
  const entry = Object.entries(FALLBACK_ALIASES).find(([, v]) => v.tab_key === 'engagement' && v.segment === segment);
  return entry ? entry[0] : null;
}

function syntheticTab(
  key: string,
  label: string,
  i18nKey: string,
  sectionKey: string,
  order: number,
  extra: Partial<ProfileLayoutTab> = {},
): ProfileLayoutTab {
  return {
    element_key: key,
    tab_key: key,
    section_key: sectionKey,
    parent_tab_key: null,
    label,
    default_label: label,
    label_override: null,
    i18n_key: i18nKey,
    display_order: order,
    default_order: order,
    is_customized: false,
    settings_columns: [],
    data_gated: true,
    always_visible: false,
    visible: true,
    sub_tabs: [],
    ...extra,
  };
}

/**
 * Add the TRUS-762 entries a pre-TRUS-762 tree does not have: the Engagement
 * hub (or its segments would have nowhere to show) and the two setup entries
 * that used to be cards on Member Profile (or they would vanish). A tree that
 * already has them is returned unchanged.
 */
export function normalizeProfileSections(sections: ProfileLayoutSection[]): ProfileLayoutSection[] {
  const has = (key: string) => sections.some((s) => s.tabs.some((t) => t.tab_key === key));
  const addHub = !has('engagement') && sections.some((s) => s.tabs.some((t) => ENGAGEMENT_SEGMENT_KEYS.includes(t.tab_key)));
  const addValidation = !has('data-validation');
  const addSystem = !has('system-information');
  if (!addHub && !addValidation && !addSystem) return sections;
  return sections.map((section) => {
    const tabs = [...section.tabs];
    if (addHub && section.section_key === 'engagement') {
      tabs.unshift(syntheticTab('engagement', 'Engagement', 'members.engagement', 'engagement', 5));
    }
    if (section.section_key === 'data') {
      if (addValidation) {
        tabs.push(syntheticTab('data-validation', 'Data Validation', 'members.dataValidation', 'data', 40, {
          data_gated: false, always_visible: true, placement: 'setup',
        }));
      }
      if (addSystem) {
        tabs.push(syntheticTab('system-information', 'System Information', 'members.systemInformation', 'data', 50, {
          data_gated: false, always_visible: true, placement: 'setup',
        }));
      }
    }
    return { ...section, tabs };
  });
}
